import {observer} from "mobx-react";
import {useStores} from "../../Hooks";
import React, {useEffect, useRef, useState} from "react";
import Utility from "../../Utility";
import FileCard from "./FileCard";
import Paginator from "../Shared/Paginator";
import {createSelectable, SelectableGroup} from "react-selectable-fast";
import PropTypes from "prop-types";
import Drop from "../DragDrop/Drop";
import { Card} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import ConditionalComponent from "../Shared/ConditionalComponent";
import LockIcon from "@mui/icons-material/Lock";
import EllipsisContainer from "../Shared/EllipsisContainer";
import Drag from "../DragDrop/Drag";
import {itemTypeEnum} from "../../enums";

const selectablePropsTypes = {
    selected: PropTypes.bool,
    dragged: PropTypes.bool.isRequired,
    somethingDragged: PropTypes.bool.isRequired,
    onCardHovered: PropTypes.func,
    onCardExited: PropTypes.func,
    openContextMenu: PropTypes.func,
    setSelected: PropTypes.func,
}

const SelectableFileCard = createSelectable(FileCard);

SelectableFileCard.propTypes = {
    file: PropTypes.object.isRequired,
    ...selectablePropsTypes
};



const FolderCard = observer((props) => {
    const {projectStore, modalStore} = useStores();
    const [isPreviewed, setIsPreviewed] = useState(false);
    const {
        selectableRef,
        isSelected,
        isSelecting,
        dragged,
        folder,
        somethingDragged,
        onCardHovered,
        onCardExited,
        setSelected,
        onDoubleClick,
        openContextMenu
    } = props;
    folder.type = itemTypeEnum.MEDIA_FOLDER;

    const isLocked = folder._id === projectStore.currentProject?.UploadFolderId;
    const {preventRefreshProject} = modalStore.modalData;
    let cardClassName = 'media-folder-card';
    if (isSelected || isSelecting) {
        cardClassName += ' selected';
    }
    if (dragged) {
        cardClassName += ' dragged';
    }

    function renderDrop() {
        return (
            <Drop itemId={folder._id}>
                <Card
                    ref={selectableRef}
                    className={cardClassName}
                    onMouseEnter={() => isSelected && onCardHovered()}
                    onMouseLeave={onCardExited}
                >
                    <div className='thumbnail-container-folder'>
                        <FolderIcon/>
                        <ConditionalComponent if={!!isLocked}>
                            <LockIcon className='lock-icon' fontSize='small'/>
                        </ConditionalComponent>
                    </div>

                    <EllipsisContainer value={folder.FolderName}>
                        <div className='file-card-file-name'>
                            {folder.FolderName}
                        </div>
                    </EllipsisContainer>
                </Card>
            </Drop>
        );
    }

    return (
        <div
            className={somethingDragged ? 'not-selectable' : ''}
            onContextMenu={(e) => {
                e.preventDefault();
                if (isLocked) {
                    return;
                }
                openContextMenu(e, folder);
            }}
            onDoubleClick={onDoubleClick}
        >
            {Utility.isMobile() || isLocked || preventRefreshProject ? (
                <div onClick={(e) => {
                    if (isPreviewed) {
                        onDoubleClick(e);
                    } else {
                        setSelected([{props}]);
                        setIsPreviewed(true);
                    }
                }}>

                    {renderDrop()}
                </div>
            ) : (
                <Drag
                    itemId={folder._id}
                    itemType={itemTypeEnum.MEDIA_FOLDER}
                    isSlave={isSelected && somethingDragged}
                >
                    {renderDrop()}
                </Drag>
            )}

        </div>
    )
});

const SelectableFolderCard = createSelectable(FolderCard);

SelectableFolderCard.propTypes = {
    folder: PropTypes.object.isRequired,
    onDoubleClick: PropTypes.func.isRequired,
    ...selectablePropsTypes
};


const TabPanel = observer(props => {
    const {generalStore, projectStore} = useStores();
    const [multiple, setMultiple] = useState(false);
    const [aboveSelectedCard, setAboveSelectedCard] = useState(false);
    const {dragStore} = useStores();
    const selectionRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 40;

    useEffect(() => {
        window.addEventListener("keydown", setMultipleSelection, false);
        window.addEventListener("keyup", unSetMultipleSelection, false);
        return () => {
            window.removeEventListener("keydown", setMultipleSelection);
            window.removeEventListener("keyup", unSetMultipleSelection);
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        setCurrentPage(1);
    }, [projectStore.mediaLibraryPathArray]);

    useEffect(() => {
        if(currentPage!==1)
            setCurrentPage(1);
        // eslint-disable-next-line
    }, [props.searchItems]);

    useEffect(() => {
        for(let i=0; i< props.files.length; i++)
        {
            if(Array.from(dragStore.selectedFilesIds.data_)[0] === props.files[i].FileId && i > currentPage*40) {
                setCurrentPage(currentPage + 1);
            }
        }
        // eslint-disable-next-line
    }, [dragStore.selectedFilesIds, props.files]);

    if (props.value !== props.index) {
        return null;
    }

    const onDrag = !!dragStore.dragItemId;
    const isReset = !aboveSelectedCard && !multiple;


    function setMultipleSelection(event) {
        if (event.key === 'Control') {
            if (!multiple) {
                setMultiple(true);
            }
        }
    }

    function unSetMultipleSelection(event) {
        if (event.key === 'Control') {
            setMultiple(false);
        }
    }

    function onItemsSelected(items) {
        generalStore.blurIntercom();
        const files = [];
        const folders = [];
        items.forEach(item => {
            if (item.props.file) {
                files.push(item.props.file);
            } else if (item.props.folder) {
                folders.push(item.props.folder);
            }
        });


        props.onFilesSelected(files);
        props.onFoldersSelected(folders);
    }

    function renderItems(items, setSearchItems) {
        return items.map((item, index) => {
            if (!item.FolderName) {
                const fileId = item.FileId;
                return Utility.isMobile() ? (
                    <FileCard
                        file={item}
                        {...getSharedSelectableProps(fileId)}
                        isSelected={dragStore.selectedFilesIds.has(fileId)}
                        key={`{${fileId}${index}`}
                    />
                ) : (
                    <SelectableFileCard
                        file={item}
                        {...getSharedSelectableProps(fileId)}
                        isSelected={dragStore.selectedFilesIds.has(fileId)}
                        key={`{${fileId}${index}`}
                    />
                );
            } else {
                const folderId = item._id;
                const folderName = item.FolderName;
                return Utility.isMobile() ? (
                    <FolderCard
                        onDoubleClick={() => {
                            props.onFolderDoubled({folderId, folderName}, [], true);
                            setSearchItems('');
                            setCurrentPage(1);
                        }}
                        folder={item}
                        isSelected={dragStore.selectedFoldersIds.has(folderId) || props?.newFolder?.current?._id === folderId}
                        {...getSharedSelectableProps(folderId)}
                        key={`{${folderId}${index}`}
                    />
                ) : (
                    <SelectableFolderCard
                        onDoubleClick={() => {
                            props.onFolderDoubled({folderId, folderName}, [], true);
                            setSearchItems('');
                            setCurrentPage(1);
                        }}
                        folder={item}
                        isSelected={dragStore.selectedFoldersIds.has(folderId) || props?.newFolder?.current?._id === folderId}
                        {...getSharedSelectableProps(folderId)}
                        key={`{${folderId}${index}`}
                    />
                );
            }
        });
    }


    function getSharedSelectableProps(itemId) {
        return {
            key: itemId,
            dragged: dragStore.dragItemId === itemId,
            somethingDragged: !!dragStore.dragItemId,
            onCardHovered: () => setAboveSelectedCard(true),
            onCardExited: () => setAboveSelectedCard(false),
            openContextMenu: (e, item) => {
                clearSelection();
                props.openContextMenu(e, item);
            },
            setSelected: onItemsSelected
        }
    }


    function clearSelection() {
        if (selectionRef.current) {
            selectionRef.current.clearSelection();
        }
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const allItems = [...props.folders, ...props.files];
    const itemsToRender = allItems.slice(startIndex, endIndex);

    function prevPage() {
        setCurrentPage(currentPage - 1);
    }

    function nextPage() {
        setCurrentPage(currentPage + 1);
    }



    return (
        <>
            <span style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "20px",
                marginLeft: "25%",
                marginTop: "-3%",
                marginRight: props.panelWidth - 300 >0 ? props.panelWidth - 300 : 15
            }}>
                    <Paginator
                        onPreviousClick={prevPage}
                        onNextClick={nextPage}
                        currentMin={currentPage * pageSize - 39}
                        currentMax={Math.min(currentPage * pageSize, allItems.length)}
                        total={allItems.length}
                    /></span>
            <div className='cards-container hide-in-mobile hide-in-tablet'>
                <SelectableGroup
                    className='selectable-container library-media'
                    style={{marginRight: props.panelWidth - 300 >0 ? props.panelWidth - 300 : 0}}
                    enableDeselect
                    globalMouse
                    onSelectionFinish={onItemsSelected}
                    selectboxClassName='select-box'
                    disabled={onDrag}
                    resetOnStart={isReset}
                    allowCtrlClick
                    allowShiftClick
                    ref={selectionRef}
                >
                    {renderItems(itemsToRender, props.setSearchItems)}
                </SelectableGroup>
            </div>
            <div className='cards-container show-in-mobile show-in-tablet'>
                {renderItems(itemsToRender, props.setSearchItems)}
            </div>
        </>
    )
});

TabPanel.propTypes = {
    value: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    files: PropTypes.array.isRequired,
    folders: PropTypes.array.isRequired,
    onFilesSelected: PropTypes.func,
    onFoldersSelected: PropTypes.func,
    onFolderDoubled: PropTypes.func,
    openContextMenu: PropTypes.func,
};

export default TabPanel;